"use client";

import Link from "next/link";
import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { ArrowRight, ShieldCheck, BarChart3, Clock, Award } from "lucide-react";

function AnimatedGauge() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    let frame: number;
    const start = performance.now();
    const duration = 1200;
    const target = 62;

    function tick(now: number) {
      const elapsed = now - start;
      const t = Math.min(elapsed / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      setProgress(Math.round(eased * target));
      if (t < 1) frame = requestAnimationFrame(tick);
    }

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [isInView]);

  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div ref={ref} className="relative w-44 h-44 mx-auto">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 128 128">
        <circle
          cx="64"
          cy="64"
          r={radius}
          fill="none"
          strokeWidth="10"
          className="stroke-ink-700"
        />
        <circle
          cx="64"
          cy="64"
          r={radius}
          fill="none"
          strokeWidth="10"
          className="stroke-yellow-500 transition-[stroke-dashoffset] duration-100"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-4xl font-black text-yellow-500">{progress}%</span>
        <span className="text-lg font-bold text-yellow-500">C</span>
      </div>
    </div>
  );
}

export default function HealthCheckCTA() {
  return (
    <section className="relative overflow-hidden bg-ink-950 py-20 sm:py-28 aura">
      <div className="relative z-10 mx-auto max-w-6xl px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2 lg:gap-12">
          {/* Left — Copy */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="ribbon mb-3 text-ink-300">Free tool</p>
            <h2 className="font-display text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl text-balance">
              What grade does your review response{" "}
              <em className="font-display italic font-normal text-sig-yellow">
                actually
              </em>{" "}
              get?
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-ink-300 text-pretty">
              Most businesses reply to fewer than half their Google reviews — and
              take days to do it. Find out where you stand in 60 seconds.
            </p>

            <ul className="mt-8 space-y-3">
              {[
                {
                  icon: BarChart3,
                  text: "Your response rate by star rating",
                },
                {
                  icon: Clock,
                  text: "Average reply speed (hours or days?)",
                },
                {
                  icon: Award,
                  text: "A letter grade from A to F",
                },
              ].map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-600/20 text-brand-300">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <span className="font-medium text-ink-200">{text}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
              <Link
                href="/tools/review-health-check"
                className="approve-btn inline-flex items-center gap-2 rounded-xl bg-white px-7 py-4 text-base font-bold text-ink-950 shadow-lg hover:bg-ink-100"
              >
                Check my review health — free
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>

            <p className="mt-5 flex items-center gap-2 text-xs text-ink-400">
              <ShieldCheck className="h-4 w-4 text-ink-500" aria-hidden="true" />
              Read-only access. Nothing on your profile is changed.
            </p>
          </motion.div>

          {/* Right — Animated mock report */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="flex justify-center"
          >
            <div className="w-full max-w-sm rounded-3xl border border-ink-700 bg-ink-900/60 p-8 shadow-2xl backdrop-blur">
              <p className="ribbon mb-6 text-center text-ink-400">Sample report</p>

              <AnimatedGauge />

              {/* Mock stats below gauge */}
              <div className="mt-8 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-ink-400">Avg. Reply Speed</span>
                  <span className="text-sm font-bold text-white">7.2 hrs</span>
                </div>
                <div className="w-full h-px bg-ink-700" />
                <div className="flex justify-between items-center">
                  <span className="text-sm text-ink-400">Unreplied Negative</span>
                  <span className="text-sm font-bold text-red-400">4</span>
                </div>
                <div className="w-full h-px bg-ink-700" />
                <div className="flex justify-between items-center">
                  <span className="text-sm text-ink-400">30-Day Trend</span>
                  <span className="text-sm font-bold text-yellow-400">58%</span>
                </div>
              </div>

              {/* Blurred tease */}
              <div className="mt-6 space-y-3 select-none" aria-hidden>
                <div className="h-3 rounded bg-ink-700/60 blur-[2px] w-full" />
                <div className="h-3 rounded bg-ink-700/60 blur-[2px] w-4/5" />
                <div className="h-3 rounded bg-ink-700/60 blur-[2px] w-3/5" />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
